import { Link } from 'react-router-dom';
import { Loader2, PackageSearch } from 'lucide-react';

import type { NexusDeliveredTask } from '../../../../domain/nexus/model';
import { CopyOnClick } from './CopyOnClick';
import {
  bugLabel,
  copyableTaskId,
  drillDownSearch,
  otherProjectIds,
  outcomeLabel,
  outcomeTone,
  pendingAttempt,
  resultsCaption,
  shortVersion,
  sourceKindLabel,
  sourceLocation,
  taskStateLabel,
} from './deliveredTaskHelpers';
import type { DeliveredTasksViewModel } from './deliveredTasksViewModel';
import { formatNexusTimestamp } from './helpers';

/** Search results: one row per delivered task ID. Bug and project cells open the drill-down. */
export function DeliveredTasksTable({ vm }: { vm: DeliveredTasksViewModel }) {
  const tasks = vm.result?.tasks ?? [];
  return (
    <section className="mt-4 overflow-hidden rounded-md border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 sm:px-5">
        <h2 className="font-semibold text-gray-900">Delivered tasks</h2>
        <p className="flex items-center gap-2 text-xs text-gray-500">
          {vm.loading && <Loader2 className="h-3 w-3 animate-spin" />}
          {resultsCaption(vm.result)}
        </p>
      </div>
      {tasks.length ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-3 font-medium sm:px-5">Task</th>
                <th className="px-3 py-3 font-medium">Status</th>
                <th className="px-3 py-3 font-medium">Bug</th>
                <th className="px-3 py-3 font-medium">Source</th>
                <th className="px-3 py-3 font-medium">Nexus projects</th>
                <th className="px-3 py-3 font-medium sm:pr-5">Delivered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tasks.map((task) => (
                <DeliveredTaskRow key={task.taskId} task={task} bugText={bugLabel(task.deliveryBugId ? vm.result?.bugs?.[task.deliveryBugId] : null)} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col items-center px-5 py-10 text-center text-gray-400">
          <PackageSearch className="h-6 w-6" />
          <p className="mt-2 text-sm font-medium text-gray-700">
            {vm.result?.query ? `No delivered tasks match “${vm.result.query}”` : 'No delivered tasks recorded yet'}
          </p>
        </div>
      )}
      {vm.canLoadMore && (
        <div className="border-t border-gray-200 px-4 py-3 text-center sm:px-5">
          <button
            type="button"
            onClick={() => void vm.loadMore()}
            disabled={vm.loading}
            className="text-sm font-medium text-primary hover:underline disabled:opacity-50"
          >
            Load more
          </button>
        </div>
      )}
    </section>
  );
}

function DeliveredTaskRow({ task, bugText }: { task: NexusDeliveredTask; bugText: string }) {
  const location = sourceLocation(task);
  const others = otherProjectIds(task);
  const pending = pendingAttempt(task);
  const state = taskStateLabel(task.taskState);
  return (
    <tr className="align-top">
      <td className="min-w-[10rem] px-3 py-3 sm:px-5">
        <CopyOnClick value={copyableTaskId(task)} label="task ID" className="-mx-1 px-1">
          <span className="block break-all font-mono text-xs font-medium text-gray-900">{task.taskId}</span>
          {task.version && (
            <span className="mt-0.5 block font-mono text-[11px] text-gray-500">@{shortVersion(task.version)}</span>
          )}
        </CopyOnClick>
        {task.testRun && <p className="mt-1 text-[11px] text-blue-600">test run</p>}
      </td>
      <td className="px-3 py-3">
        <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${outcomeTone(task.status)}`}>
          {outcomeLabel(task.status)}
        </span>
        {state && <p className="mt-1 whitespace-nowrap text-[11px] text-gray-500">{state}</p>}
        {pending && (
          <p className="mt-1 max-w-[12rem] text-[11px] text-amber-700">
            Newer attempt pending in <span className="font-mono">{pending.remoteProjectId || pending.deliveryId}</span>
          </p>
        )}
      </td>
      <td className="px-3 py-3 text-xs">
        {task.deliveryBugId ? (
          <Link to={{ search: drillDownSearch({ bug: task.deliveryBugId }) }} className="font-mono text-primary hover:underline">{task.deliveryBugId}</Link>
        ) : (
          <p className="text-gray-400">—</p>
        )}
        {bugText && <p className="mt-0.5 text-[11px] text-gray-500">{bugText}</p>}
      </td>
      <td className="max-w-[16rem] px-3 py-3 text-xs">
        {task.sourceTaskId && task.sourceTaskId !== task.taskId && (
          <p className="break-all font-mono text-gray-900">{task.sourceTaskId}</p>
        )}
        {task.synthPipelineId && task.synthPipelineId !== task.sourceTaskId && (
          <p className="mt-0.5 break-all font-mono text-[11px] text-gray-500">{task.synthPipelineId}</p>
        )}
        <p className="mt-0.5 text-[11px] text-gray-500">{sourceKindLabel(task.sourceKind)}</p>
        {location && (
          <CopyOnClick value={location} label="source location" className="-mx-1 mt-0.5 px-1">
            <span className="block truncate font-mono text-[11px] text-gray-500">{location}</span>
          </CopyOnClick>
        )}
      </td>
      <td className="px-3 py-3 text-xs">
        {task.currentProjectId && task.deliveryBugId && task.currentDeliveryId ? (
          <Link
            to={{ search: drillDownSearch({ bug: task.deliveryBugId, project: task.currentDeliveryId }) }}
            className="font-mono font-medium text-primary hover:underline"
          >
            {task.currentProjectId}
          </Link>
        ) : task.currentProjectId ? (
          <p className="font-mono font-medium text-gray-900">{task.currentProjectId}</p>
        ) : (
          <p className="text-gray-400">—</p>
        )}
        {others.length > 0 && (
          <p className="mt-0.5 font-mono text-[11px] text-gray-500" title={others.join(', ')}>
            also {others.slice(0, 3).join(', ')}{others.length > 3 ? ` +${others.length - 3}` : ''}
          </p>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500 sm:pr-5">
        {formatNexusTimestamp(task.currentSubmittedAt)}
        {task.firstSubmittedAt && task.firstSubmittedAt !== task.currentSubmittedAt && (
          <p className="mt-0.5 text-[11px] text-gray-400">first {formatNexusTimestamp(task.firstSubmittedAt)}</p>
        )}
      </td>
    </tr>
  );
}
